import { useEffect, useMemo, useRef, useState } from 'react';
import type { GalleryFileEntry } from '../types/moderation';
import { getProcessedUrl } from '../lib/media';
import { getSignedUrl } from '../lib/api';
import { parseExternalVideoUrl } from './MediaGallery';

type FileKind = 'image' | 'pdf' | 'audio' | 'video' | 'other';

interface MediaCarouselProps {
  submissionId: string;
  files: GalleryFileEntry[];
  /** Optional external video URL (YouTube / Vimeo) appended as the first slide. */
  externalVideoUrl?: string;
  /** Optional transcript shown beneath audio/video slides. */
  transcript?: string;
}

function detectFileKind(file: GalleryFileEntry): FileKind {
  const format = (
    file.processing?.format ||
    file.contentType ||
    ''
  ).toLowerCase();
  const key = (
    file.processedKey ||
    file.preservation?.originalKey ||
    ''
  ).toLowerCase();
  if (
    format.startsWith('image/') ||
    /\.(jpe?g|png|gif|webp|tiff?|heic|avif)$/i.test(key)
  ) {
    return 'image';
  }
  if (format.includes('pdf') || /\.pdf$/i.test(key)) return 'pdf';
  if (
    format.startsWith('audio/') ||
    /\.(mp3|wav|ogg|m4a|aac)$/i.test(key)
  ) {
    return 'audio';
  }
  if (
    format.startsWith('video/') ||
    /\.(mp4|webm|mov|m4v)$/i.test(key)
  ) {
    return 'video';
  }
  return 'other';
}

type Slide =
  | { kind: 'external'; url: string }
  | { kind: 'file'; file: GalleryFileEntry; fileKind: FileKind; index: number };

/**
 * Horizontal swipe carousel for a submission's media files.
 *
 * - Tracks the current slide as state; the inner track is translated via
 *   `transform: translateX(-N * 100%)` so each slide takes the full width
 *   of the viewport.
 * - Touch handlers compute the horizontal delta on touchend and snap to
 *   the next / previous slide when the user drags beyond 60 px.
 * - Vertical scroll is preserved (`touch-action: pan-y`) so a thumb swipe
 *   down through the page does not get hijacked by the carousel.
 * - For non-image files we lazily fetch a signed S3 URL only when the
 *   slide becomes active, so opening a record with five videos does not
 *   trigger five signed-URL round trips immediately.
 * - Pinch-to-zoom on images is delegated to the browser via the native
 *   image element and `touch-action: pinch-zoom` on the slide container.
 */
export default function MediaCarousel({
  submissionId,
  files,
  externalVideoUrl,
  transcript,
}: MediaCarouselProps) {
  // Memoise the slide list so the signed-URL effect below has a stable
  // dependency. Otherwise the effect would re-run on every render and
  // its cleanup would cancel the in-flight fetch.
  const slides = useMemo<Slide[]>(() => {
    const list: Slide[] = [];
    if (externalVideoUrl) {
      list.push({ kind: 'external', url: externalVideoUrl });
    }
    files.forEach((file, index) => {
      list.push({
        kind: 'file',
        file,
        fileKind: detectFileKind(file),
        index,
      });
    });
    return list;
  }, [files, externalVideoUrl]);

  const [active, setActive] = useState(0);
  const [signedUrls, setSignedUrls] = useState<Record<number, string>>({});
  const [signedUrlErrors, setSignedUrlErrors] = useState<Record<number, string>>(
    {},
  );
  const [signedUrlLoading, setSignedUrlLoading] = useState<Record<number, boolean>>(
    {},
  );

  // Track which file-indexes we've already begun fetching so the effect
  // doesn't double-fire when state updates re-render the component.
  const fetchStarted = useRef<Set<number>>(new Set());

  const trackRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const horizontalIntent = useRef<boolean>(false);

  // Reset the active slide when the file list changes (e.g. a detach
  // removed one). Clamp to the new bounds and clear cached URLs so we
  // refetch against the new file set.
  useEffect(() => {
    setActive((prev) => Math.min(prev, Math.max(0, slides.length - 1)));
    fetchStarted.current = new Set();
    setSignedUrls({});
    setSignedUrlErrors({});
    setSignedUrlLoading({});
  }, [files, slides.length]);

  // Lazy-fetch signed URLs for non-image, non-external slides as they
  // become active. We track started fetches in a ref so the effect
  // doesn't double-fire when state updates re-render the component.
  useEffect(() => {
    const slide = slides[active];
    if (!slide || slide.kind !== 'file') return;
    if (slide.fileKind === 'image') return;
    const idx = slide.index;
    if (fetchStarted.current.has(idx)) return;
    fetchStarted.current.add(idx);
    setSignedUrlLoading((m) => ({ ...m, [idx]: true }));
    setSignedUrlErrors((m) => ({ ...m, [idx]: '' }));
    getSignedUrl(submissionId, idx)
      .then((res) => {
        setSignedUrls((m) => ({ ...m, [idx]: res.url }));
      })
      .catch((err: unknown) => {
        setSignedUrlErrors((m) => ({
          ...m,
          [idx]:
            (err as Error).message || 'Could not load this file.',
        }));
      })
      .finally(() => {
        setSignedUrlLoading((m) => ({ ...m, [idx]: false }));
      });
  }, [active, slides, submissionId]);

  const goTo = (next: number) => {
    if (next < 0 || next >= slides.length) return;
    setActive(next);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    horizontalIntent.current = false;
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    // Once horizontal motion clearly dominates, lock the gesture so we
    // don't ping-pong between the carousel and the page scroll.
    if (!horizontalIntent.current && Math.abs(dx) > 16 && Math.abs(dx) > Math.abs(dy)) {
      horizontalIntent.current = true;
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    if (!t) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }
    const dx = t.clientX - touchStartX.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (!horizontalIntent.current) return;
    if (dx <= -60) goTo(active + 1);
    else if (dx >= 60) goTo(active - 1);
  };

  if (slides.length === 0) {
    return (
      <div
        className="media-carousel media-carousel--empty"
        data-testid="media-carousel-empty"
      >
        <p>No files attached.</p>
      </div>
    );
  }

  return (
    <div className="media-carousel" data-testid="media-carousel">
      <div
        className="media-carousel__track"
        ref={trackRef}
        style={{
          transform: `translateX(-${active * 100}%)`,
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        data-testid="media-carousel-track"
      >
        {slides.map((slide, i) => (
          <div
            className="media-carousel__slide"
            key={i}
            aria-hidden={i !== active}
            data-testid={`media-carousel-slide-${i}`}
          >
            <SlideBody
              slide={slide}
              signedUrl={
                slide.kind === 'file' ? signedUrls[slide.index] : undefined
              }
              signedUrlLoading={
                slide.kind === 'file' ? !!signedUrlLoading[slide.index] : false
              }
              signedUrlError={
                slide.kind === 'file' ? signedUrlErrors[slide.index] : undefined
              }
              transcript={transcript}
            />
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="media-carousel__nav media-carousel__nav--prev"
            aria-label="Previous file"
            onClick={() => goTo(active - 1)}
            disabled={active === 0}
            data-testid="carousel-prev"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path
                d="M11 4L6 9l5 5"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="media-carousel__nav media-carousel__nav--next"
            aria-label="Next file"
            onClick={() => goTo(active + 1)}
            disabled={active === slides.length - 1}
            data-testid="carousel-next"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path
                d="M7 4l5 5-5 5"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div
            className="media-carousel__dots"
            role="tablist"
            aria-label="Files"
            data-testid="carousel-dots"
          >
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`Show file ${i + 1} of ${slides.length}`}
                className={`media-carousel__dot${
                  i === active ? ' media-carousel__dot--active' : ''
                }`}
                onClick={() => goTo(i)}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}

      <div className="media-carousel__counter" data-testid="carousel-counter">
        {active + 1} / {slides.length}
      </div>
    </div>
  );
}

interface SlideBodyProps {
  slide: Slide;
  signedUrl?: string;
  signedUrlLoading: boolean;
  signedUrlError?: string;
  transcript?: string;
}

function SlideBody({
  slide,
  signedUrl,
  signedUrlLoading,
  signedUrlError,
  transcript,
}: SlideBodyProps) {
  if (slide.kind === 'external') {
    const match = parseExternalVideoUrl(slide.url);
    if (!match) {
      return (
        <div className="media-carousel__placeholder" data-testid="carousel-external-link">
          <a href={slide.url} target="_blank" rel="noreferrer">
            Open video link
          </a>
        </div>
      );
    }
    return (
      <div className="media-carousel__embed" data-testid="carousel-external-embed">
        <iframe
          src={match.embedUrl}
          title={`${match.provider} video`}
          className="media-carousel__iframe"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  const { file, fileKind } = slide;

  if (fileKind === 'image') {
    return (
      <img
        src={getProcessedUrl(file)}
        alt={file.altText || file.filename || ''}
        className="media-carousel__image"
        loading="lazy"
        data-testid="carousel-image"
      />
    );
  }

  if (signedUrlLoading) {
    return (
      <div
        className="media-carousel__placeholder"
        data-testid="carousel-loading"
      >
        Loading preview…
      </div>
    );
  }
  if (signedUrlError) {
    return (
      <div
        className="media-carousel__placeholder media-carousel__placeholder--error"
        role="alert"
        data-testid="carousel-error"
      >
        {signedUrlError}
      </div>
    );
  }
  if (!signedUrl) {
    return (
      <div
        className="media-carousel__placeholder"
        data-testid="carousel-placeholder"
      >
        Preview not available for this file type.
      </div>
    );
  }

  if (fileKind === 'pdf') {
    return (
      <div className="media-carousel__pdf-wrap" data-testid="carousel-pdf">
        <iframe
          src={signedUrl}
          title={file.filename || 'PDF preview'}
          className="media-carousel__pdf"
        />
        <a
          href={signedUrl}
          target="_blank"
          rel="noreferrer"
          className="media-carousel__pdf-link"
        >
          Open PDF in new tab ↗
        </a>
      </div>
    );
  }
  if (fileKind === 'audio') {
    return (
      <div className="media-carousel__audio-wrap" data-testid="carousel-audio">
        <audio controls src={signedUrl} className="media-carousel__audio">
          Your browser does not support the audio element.
        </audio>
        {transcript && (
          <details className="media-carousel__transcript">
            <summary>Transcript</summary>
            <pre>{transcript}</pre>
          </details>
        )}
      </div>
    );
  }
  if (fileKind === 'video') {
    return (
      <div className="media-carousel__video-wrap" data-testid="carousel-video">
        <video
          controls
          playsInline
          src={signedUrl}
          className="media-carousel__video"
        >
          Your browser does not support the video element.
        </video>
        {transcript && (
          <details className="media-carousel__transcript">
            <summary>Transcript</summary>
            <pre>{transcript}</pre>
          </details>
        )}
      </div>
    );
  }
  return (
    <div className="media-carousel__placeholder" data-testid="carousel-download">
      <a href={signedUrl} target="_blank" rel="noreferrer">
        Download file ↗
      </a>
    </div>
  );
}
